import type { BirtvyImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import { markStageFailed } from './stageSignals';

export interface ImageBodyProps {
  image: BirtvyImage;
  /** The row key, so a source that will not load records itself as this item's failure. */
  itemKey: string;
}

/**
 * A picture on the stage.
 *
 * The frame is already the bitmap's own size — `stageFor` reports the MEASURED pixel dimensions, not
 * the canvas insert box — so filling it is exact and `contain` never has anything to letterbox. It is
 * still `contain` rather than `fill`, because the first paint happens at the provisional insert-box
 * aspect while the measurement is in flight, and a wrong aspect should letterbox rather than stretch
 * somebody's photograph.
 *
 * `image.label` is what the picture was fetched by — "Figma", "solar panels at sunrise" — which is a
 * real alt text when it exists. When it does not, an empty one is correct: the image is the content
 * and the row beside it already names the file, so an invented description would be worse than none.
 */
export function ImageBody({ image, itemKey }: ImageBodyProps) {
  return (
    <img
      src={image.src}
      alt={image.label ?? ''}
      draggable={false}
      onError={() => markStageFailed(itemKey)}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
        userSelect: 'none',
      }}
    />
  );
}
